<template>
  <v-container class="pb-0">
    <v-data-table
      :headers="headers"
      :loading="comments.loading"
      :items="comments.items[type]"
      :sort-by="['pinned','created']"
      :sort-desc="[true, true]"
      multi-sort
      height="350"
      fixed-header
      class="comment-table"
      style="margin-top: -15px; z-index: 0; position: relative; margin-bottom: 0;"
    >
      <template v-slot:item.author="{ item }">

        <v-badge
          color="grey lighten-4"
          class="text--black"
          inline
          :content="item.role"
        >
          {{ item.author }}
        </v-badge>

        <template v-if="item.is_draft">
          <br />
          <v-badge
            content="Draft"
            color="error white--text"
          />
        </template>
      </template>
      <template v-slot:item.comment="{ item }">
        <span class="ql-editor" style="padding: 0; margin: 0" v-html="$sanitize(item.comment)"></span>
      </template>
      <template v-slot:item.action="{ item }">
        <div style="white-space: nowrap">
          <v-icon v-if="canEditOrDelete" small @click="editItem(item)">
            edit
          </v-icon>
          <v-icon v-if="canEditOrDelete" small @click="deleteItem(item)">
            delete
          </v-icon>
          <v-icon small @click="togglePinned(item)" :style="[isPinned(item) ? {color: 'black'} : {color: 'gray'} ]">
            {{ pinnedIcon(item) }}
          </v-icon>
        </div>
      </template>
      <template v-slot:no-data>
        <v-btn @click="$store.dispatch('account/loadComments', { account: activeAccount.account, type: type })" icon fab class="mt-4">
          <v-icon x-large>refresh</v-icon>
        </v-btn>
      </template>
      <template v-slot:footer.page-text="{ pageStart , pageStop , itemsLength }">
        {{ pageStart }} - {{ pageStop }} of {{ itemsLength }}
        <v-btn @click="$store.dispatch('account/loadComments', { account: activeAccount.account, type: type })" icon style="margin-right: -35px;">
          <v-icon>refresh</v-icon>
        </v-btn>
      </template>


      <template v-slot:footer.prepend>
        <v-row class="pl-4 mt-2">
          <mail-dialog></mail-dialog>

          <v-btn
            v-if="withManualNotificationButton && $store.state.auth.checkAccess({ key: 'manual_notifications', value: true })"
            color="info"
            small
            dark
            class="mt-1 ml-3"
            @click="$emit('show-manual-notification')"
          >
            Notification
          </v-btn>

          <v-dialog v-model="dialog" max-width="600px" style="overflow: initial">
            <template v-slot:activator="{ on }">
              <v-btn color="primary" dark class="mb-2 ml-1" v-on="on" icon>
                <v-icon>add</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-text class="pa-0">
                <v-container class="pb-0 pt-0">
                  <v-row>
                    <v-col cols="12" class="pb-0 pt-0">

                      <v-tabs background-color="transparent" slider-color="white" :centered="true">
                        <v-tab>
                          Preview
                        </v-tab>
                        <v-tab>
                          Source
                        </v-tab>
                        <v-tab-item>
                          <vue-editor v-model="editedItem.comment" :editor-toolbar="editorToolbar" />
                        </v-tab-item>
                        <v-tab-item>
                          <v-textarea v-model="editedItem.comment" label="Comment"></v-textarea>
                        </v-tab-item>
                      </v-tabs>

                    </v-col>
                  </v-row>
                  <v-checkbox
                    v-if="!editedItem.comment_id"
                    v-model="isMultipleAccounts"
                    hide-details
                    label="Post this comment to multiple accounts"
                  />
                  <v-combobox
                    v-if="isMultipleAccounts"
                    v-model="multipleAccountIDs"
                    class="mb-2"
                    multiple
                    small-chips
                    :delimiters="[',']"
                    clearable
                    hint="Press ENTER to add account id"
                    @change="updateAccountIds"
                    label="Account IDs"
                  />
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="blue darken-1"
                  text
                  :loading="isLoading"
                  @click="close"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  :loading="isLoading"
                  @click="save"
                >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </v-row>
      </template>

    </v-data-table>
  </v-container>
</template>

<style>
  .comment-table td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  .comment-table td .v-badge .v-badge__badge {
    color: rgba(0, 0, 0, 0.5);
  }

  .comment-table td > span > p {
    padding: 0;
    margin: 0;
  }

  .comment-table .ql-editor {
    font-size: inherit !important;
  }

  .ql-editor .ql-align-center {
    text-align: center;
  }

  .ql-editor .ql-align-left {
    text-align: left;
  }

  .ql-editor .ql-align-right {
    text-align: right;
  }

  .ql-editor .ql-align-justify {
    text-align: justify;
  }
</style>

<script>

  import { VueEditor } from 'vue2-editor'
  import { API } from '@/utils/api'
  import { mapGetters } from 'vuex'
  import { get, uniq } from 'lodash'

  const COMMENT_UPDATE = '/$v/account/comments/update.json'
  const COMMENT_ADD_MANY = '/$v/account/comments/post_multiple.json'

  export default {

    components: {
      MailDialog: () => import('@/components/user/mail/Dialog'),
      VueEditor
    },
    props: {
      type: {
        type: String,
      },
      withManualNotificationButton: {
        type: Boolean,
      },
      isActionAllowed: {
        type: Boolean,
        default: true,
      }
    },

    data: () => ({
      dialog: false,
      headers: [
        { text: 'Date', value: 'created' },
        { text: 'Author', value: 'author', sortable: false },
        { text: 'Comment', align: 'left', sortable: false, value: 'comment', cls: [ 'comment-slot' ] },
        { text: 'Actions', value: 'action', sortable: false, width: '75px' }
      ],
      editedIndex: -1,
      editedItem: {
        comment: '',
        author: '',
        date: '',
        comment_id: 0,
      },
      defaultItem: {
        comment: '',
        author: '',
        date: '',
        comment_id: 0,
      },
      editorToolbar: [
        [ { header: [ false, 1, 2, 3, 4, 5, 6 ] } ],
        [ 'bold', 'italic', 'underline', 'strike' ],
        [ { color: [] } ],
        [ { list: 'ordered' }, { list: 'bullet' }, { align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' } ],
        [ 'link', 'code-block' ]
      ],
      isMultipleAccounts: false,
      multipleAccountIDs: [],
      isLoading: false,
    }),

    computed: {
      ...mapGetters({ activeAccount: 'account/active', comments: 'account/comments' }),
      formTitle () {
        return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
      },
      canEditOrDelete () {
        return this.isActionAllowed || !this.$store.state.auth.checkAccess({ key: 'deny_edit_comments', value: true })
      },
    },

    watch: {
      dialog (val) {
        val || this.close()
      }
    },

    mounted () {
    },

    created () {
    },

    methods: {
      get,
      updateAccountIds (ids) {
        this.multipleAccountIDs = [ ...new Set(ids.join(',').replace(/\s/g, '').split(',').filter(i => !!i)) ]
      },

      editItem (item) {
        this.editedIndex = this.comments.items[this.type].indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.dialog = true
      },

      isPinned (item) {
        return item.pinned != '0000-00-00 00:00:00' && item.pinned != null
      },
      pinnedIcon (item) {
        return this.isPinned(item) ? 'mdi-pin' : 'mdi-pin-off'
      },

      togglePinned (item) {

        let index = this.comments.items[this.type].indexOf(item)

        API.call('/$v/account/comments/pin_toggle.json', {
          login: this.activeAccount.account.login,
          comment_id: item.comment_id,
        }, this.activeAccount.account).then(resp => {

          this.comments.items[this.type][index].pinned = resp.data.pinned

          return resp.data
        }).catch(e => {
        })

      },

      async deleteItem (item) {
        if (await this.$root.$confirm('Confirm', 'Are you sure you want to delete this item?')) {

          API.call('/$v/account/comments/delete.json', {
            login: this.activeAccount.account.login,
            comment_id: item.comment_id,
          }, this.activeAccount.account).then(resp => {
            return this.$store.dispatch('account/loadComments', { account: this.activeAccount.account, type: this.type })
          }).catch(e => {
          })

        }
      },

      close () {
        this.dialog = false
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.isMultipleAccounts = false
          this.multipleAccountIDs = []
        }, 300)
      },

      async save () {
        const commonCommentPayload = {
          type: this.type,
          comment_id: this.editedItem.comment_id,
          comment: this.editedItem.comment,
        }
        this.isLoading = true

        let promise = Promise.resolve()
        if (this.isMultipleAccounts) {
          const thisAccountIdStr = `${this.activeAccount.account.account_id}`
          const account_ids = uniq([...this.multipleAccountIDs, thisAccountIdStr])

          if (account_ids.length) {
            promise = API.call(COMMENT_ADD_MANY, {
              ...commonCommentPayload,
              account_ids
            })
          }
        } else {
          promise = API.call(COMMENT_UPDATE, {
            ...commonCommentPayload,
            login: this.activeAccount.account.login,
          }, this.activeAccount.account)
        }

        promise
          .then(resp => this.$store.dispatch('account/loadComments', { account: this.activeAccount.account, type: this.type }))
          .then(r => this.close())
          .catch(e => {
          })
          .finally(() => this.isLoading = false)
      },
    },
  }
</script>
